import type {
	AiQueryRequest,
	AiQueryRequestUrlParams,
	GetAskConversationResponseJson,
	GetAskConversationsResponseJson,
	GetCompanyAISettingsResponse,
	HasAccessibleDocumentsResponse,
	PatchCompanyAISettingsRequest,
	PatchCompanyAISettingsResponse,
	PostCompanyAISettingsRequest,
	PostCompanyAISettingsResponse,
	StartCompetencyImportResponse,
	UserErrorResponseJson,
} from "@performyard/service-contracts";

import { queryClient } from "../../../queryClient";
import { MeResponse } from "../auth";
import internalRequest, { ErrorResponse, getUrl } from "./internal-base-request";

export const getCompanyAISettings = async () => {
	const [response] = await internalRequest<GetCompanyAISettingsResponse>("/v1/ai/settings");
	return response;
};

export const postCompanyAISettings = async (aiSettings: PostCompanyAISettingsRequest) => {
	const [response] = await internalRequest<PostCompanyAISettingsResponse>("/v1/ai/settings", {
		method: "POST",
		body: JSON.stringify({ ...aiSettings }),
	});

	return response;
};

export const patchCompanyAISettings = async (aiSettings: PatchCompanyAISettingsRequest) => {
	const [response] = await internalRequest<PatchCompanyAISettingsResponse>("/v1/ai/settings", {
		method: "PATCH",
		body: JSON.stringify({ ...aiSettings }),
	});

	return response;
};

export const getHasAccessibleDocuments = async (): Promise<HasAccessibleDocumentsResponse> => {
	const [response] = await internalRequest<HasAccessibleDocumentsResponse>("/v1/ai/documents/accessible");
	return response;
};

export const startCompetencyImport = async (file: File): Promise<StartCompetencyImportResponse> => {
	const formData = new FormData();
	formData.append("file", file);
	const currentUser = queryClient.getQueryData(["auth-current-user"]) as MeResponse;
	if (!currentUser) {
		throw new Error("Current user not found");
	}
	const res = await fetch(getUrl("/v1/ai/competency-import/parse"), {
		method: "POST",
		headers: {
			"X-CSRFToken": window.xsrf_val,
			Authorization: `Bearer ${currentUser.me.authorization?.token}`,
		},
		body: formData,
	});
	const data = await res.json().catch(() => null);
	if (!res.ok) {
		throw new ErrorResponse(
			(data as { message?: string } | null)?.message ??
				"We couldn't read this file. Try a different PDF, or paste your competency information directly into the chat.",
			res,
			data
		);
	}
	return data as StartCompetencyImportResponse;
};

/** Saved Ask PerformYard conversations for the current user, most recently updated first (PY-13559). */
export const getAskConversations = async (props: {
	limit?: number;
	cursor?: string;
	search?: string;
}): Promise<GetAskConversationsResponseJson> => {
	const params = new URLSearchParams();
	if (props.limit !== undefined) params.set("limit", String(props.limit));
	if (props.cursor) params.set("cursor", props.cursor);
	// Only sent when non-empty — an empty `search` would still be a distinct
	// query string, splitting the cache from the unfiltered list for no reason.
	if (props.search?.trim()) params.set("search", props.search.trim());

	const query = params.toString();
	const [response] = await internalRequest<GetAskConversationsResponseJson>(
		`/v1/ai/conversations${query ? `?${query}` : ""}`
	);
	return response;
};

/** One conversation with its full message thread, for restoring it into the panel. */
export const getAskConversation = async (conversationId: string): Promise<GetAskConversationResponseJson> => {
	const [response] = await internalRequest<GetAskConversationResponseJson>(
		`/v1/ai/conversations/${encodeURIComponent(conversationId)}`
	);
	return response;
};

type AiQueryRequestProps = AiQueryRequest & AiQueryRequestUrlParams;

export const postQueryAi = async <TResponse>(
	props: AiQueryRequestProps
): Promise<TResponse | UserErrorResponseJson> => {
	// Start the POST request to the streaming endpoint we need to catch because internalRequest throws an error and doesn't handle it properly
	try {
		const [response] = await internalRequest<TResponse | UserErrorResponseJson>(
			`/v1/ai/prompt?retry=${props.retry || false}&responseStyle=${props.responseStyle || "text"}`,
			{
				method: "POST",
				body: JSON.stringify(props),
			}
		);

		return response;
	} catch (error) {
		// internalRequest throws ErrorResponse on non-2xx; its `.data` field carries the backend body.
		// Forward the backend's UserErrorResponse so the user sees the real error message
		// (e.g. ContentTooLargeForAiError's message). Fall back to a generic one when the
		// thrown object doesn't carry a usable backend body.
		if (
			error instanceof ErrorResponse &&
			error.data &&
			typeof (error.data as { message?: unknown }).message === "string"
		) {
			return error.data as UserErrorResponseJson;
		}
		return {
			message: "Something went wrong",
			details: [error instanceof Error ? error.message : "Unknown error"],
		};
	}
};
